/**
 * Safely format date strings, handling various input formats and invalid dates
 */
export const formatDate = (dateValue: any, options?: Intl.DateTimeFormatOptions): string => {
  // Add debug logging to see what we're getting
  // console.log('formatDate called with:', dateValue, typeof dateValue);
  
  if (!dateValue || dateValue === '' || dateValue === 'undefined' || dateValue === 'null') {
    // console.log('formatDate: no valid dateValue provided');
    return 'N/A';
  }

  try {
    let date: Date;

    // Handle Firestore Timestamp objects
    if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
      // console.log('formatDate: handling Firestore timestamp');
      date = dateValue.toDate();
    }
    // Handle already parsed Date objects
    else if (dateValue instanceof Date) {
      // console.log('formatDate: handling Date object');
      date = dateValue;
    }
    // Handle string dates
    else if (typeof dateValue === 'string') {
      // console.log('formatDate: handling string date');
      // Try to handle various string formats
      if (dateValue.includes('T') || dateValue.includes('-') || dateValue.includes('/')) {
        date = new Date(dateValue);
      } else {
        // If it's just a number as string, parse as timestamp
        const timestamp = parseInt(dateValue);
        if (!isNaN(timestamp)) {
          date = new Date(timestamp);
        } else {
          date = new Date(dateValue);
        }
      }
    }
    // Handle numeric timestamps
    else if (typeof dateValue === 'number') {
      // console.log('formatDate: handling numeric timestamp');
      // Handle both milliseconds and seconds timestamps
      date = new Date(dateValue > 1000000000000 ? dateValue : dateValue * 1000);
    }
    // Handle Firestore Timestamp-like objects with seconds/nanoseconds
    else if (dateValue && typeof dateValue === 'object' && dateValue._seconds) {
      // console.log('formatDate: handling Firestore timestamp object with _seconds');
      date = new Date(dateValue._seconds * 1000);
    }
    // Handle objects with seconds property
    else if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      // console.log('formatDate: handling timestamp object with seconds');
      date = new Date(dateValue.seconds * 1000);
    }
    // Handle objects with nanoseconds (Firestore format)
    else if (dateValue && typeof dateValue === 'object' && dateValue._nanoseconds !== undefined) {
      // console.log('formatDate: handling Firestore timestamp with nanoseconds');
      const milliseconds = Math.floor(dateValue._nanoseconds / 1000000);
      date = new Date((dateValue._seconds || 0) * 1000 + milliseconds);
    }
    // Fallback for other formats
    else {
      // console.log('formatDate: fallback parsing');
      date = new Date(dateValue);
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('formatDate: Invalid date after parsing:', dateValue);
      return 'N/A'; // Changed from "Invalid Date" to "N/A"
    }

    // console.log('formatDate: successfully parsed to:', date);
    // Use provided options or default locale formatting
    const formatted = date.toLocaleDateString(undefined, options || {
      year: 'numeric',
      month: 'short', 
      day: 'numeric'
    });
    // console.log('formatDate: formatted result:', formatted);
    return formatted;
  } catch (error) {
    console.warn('Date formatting error:', error, 'for value:', dateValue);
    return 'N/A'; // Changed from "Invalid Date" to "N/A"
  }
};

/**
 * Format date with time
 */
export const formatDateTime = (dateValue: any): string => {
  return formatDate(dateValue, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format date in a more readable format
 */
export const formatDateLong = (dateValue: any): string => {
  return formatDate(dateValue, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};